import React, { useEffect, useState, useLayoutEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import OtherFeesInput from "../../../shared/OtherFeesInput";
import {
  CustomLoading,
  CustomModal,
  CustomButton,
  CustomTextInput,
  CustomTwoColInputGroup,
  CustomCalendarInput,
  CustomSingleSelect,
} from "@ippon/shared-components";
import { useForm } from "react-hook-form";
import {
  calculateContractMonthlyAmount,
  calculateTotalTsuboUnitPrice,
} from "@ippon/shared-utils";
import { Can } from "@/components/auth/Can";
import { useTsuboLinkedAmounts } from "@/hooks/useTsuboLinkedAmounts";

interface EditContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractorType: "individual" | "corporate";
  initialData?: Partial<EditContractFormData>;
  // "deferred" を返すと、このモーダルは閉じずに開いたままにする
  // (顧客マスタ波及の確認モーダルを上に重ねて出すケース)。
  onSubmit?: (
    data: EditContractFormData,
  ) => void | "deferred" | Promise<void | "deferred">;
  isAncillary?: boolean;
  areaTsubo?: number;
}

export type ContractorType = "individual" | "corporate";

export interface EditContractFormData {
  contractorType: ContractorType;
  // 個人
  tenantLastName: string;
  tenantFirstName: string;
  tenantPhone: string;
  tenantEmail: string;
  // 法人
  companyName: string;
  representativeLastName: string;
  representativeFirstName: string;
  companyPhone: string;
  companyEmail: string;
  // 契約情報
  startDate: string;
  endDate: string;
  contract_form: string;
  rentStartDate: string;
  cancellationDate: string;
  monthlyRent: string;
  rentUnitPrice: string;
  maintenanceFee: string;
  maintenanceFeeUnitPrice: string;
  totalRentUnitPrice: string;
  pricingType: "fixed" | "unit_quantity";
  unitPrice: string;
  quantity: string;
  quantityUnit: string;
  deposit: string;
  depositAfter: string;
  keyMoney: string;
  note: string;
}

const EditContractModal: React.FC<EditContractModalProps> = ({
  isOpen,
  onClose,
  contractorType: initialContractorType,
  initialData,
  onSubmit,
  isAncillary = false,
  areaTsubo,
}) => {
  const [contractorType, setContractorType] = useState<ContractorType>(
    initialContractorType,
  );
  const [otherFees, setOtherFees] = useState<
    Array<{ label: string; amount: string }>
  >([]);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<EditContractFormData>({
    defaultValues: {
      contractorType: initialContractorType,
      tenantLastName: "",
      tenantFirstName: "",
      tenantPhone: "",
      tenantEmail: "",
      companyName: "",
      representativeLastName: "",
      representativeFirstName: "",
      companyPhone: "",
      companyEmail: "",
      startDate: "",
      endDate: "",
      contract_form: "",
      rentStartDate: "",
      cancellationDate: "",
      monthlyRent: "",
      rentUnitPrice: "",
      maintenanceFee: "",
      maintenanceFeeUnitPrice: "",
      totalRentUnitPrice: "",
      pricingType: "fixed",
      unitPrice: "",
      quantity: "1",
      quantityUnit: "台",
      deposit: "",
      depositAfter: "",
      keyMoney: "",
      note: "",
    },
  });

  useLayoutEffect(() => {
    if (isOpen) {
      const type = initialData?.contractorType || initialContractorType;
      setContractorType(type);
      form.reset({
        contractorType: type,
        tenantLastName: initialData?.tenantLastName || "",
        tenantFirstName: initialData?.tenantFirstName || "",
        tenantPhone: initialData?.tenantPhone || "",
        tenantEmail: initialData?.tenantEmail || "",
        companyName: initialData?.companyName || "",
        representativeLastName: initialData?.representativeLastName || "",
        representativeFirstName: initialData?.representativeFirstName || "",
        companyPhone: initialData?.companyPhone || "",
        companyEmail: initialData?.companyEmail || "",
        startDate: initialData?.startDate || "",
        endDate: initialData?.endDate || "",
        contract_form: initialData?.contract_form || "",
        rentStartDate: initialData?.rentStartDate || "",
        cancellationDate: initialData?.cancellationDate || "",
        monthlyRent: initialData?.monthlyRent || "",
        rentUnitPrice: initialData?.rentUnitPrice || "",
        maintenanceFee: initialData?.maintenanceFee || "",
        maintenanceFeeUnitPrice: initialData?.maintenanceFeeUnitPrice || "",
        totalRentUnitPrice: "",
        pricingType: initialData?.pricingType || "fixed",
        unitPrice: initialData?.unitPrice || "",
        quantity: initialData?.quantity || "1",
        quantityUnit: initialData?.quantityUnit || "台",
        deposit: initialData?.deposit || "",
        depositAfter: initialData?.depositAfter || "",
        keyMoney: initialData?.keyMoney || "",
        note: initialData?.note || "",
      });
      setOtherFees((initialData as any)?.otherFees || []);
    }
  }, [isOpen, initialContractorType, initialData]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  useTsuboLinkedAmounts({
    form,
    areaTsubo,
    monthlyField: "monthlyRent",
    unitPriceField: "rentUnitPrice",
    enabled: !isAncillary,
  });
  useTsuboLinkedAmounts({
    form,
    areaTsubo,
    monthlyField: "maintenanceFee",
    unitPriceField: "maintenanceFeeUnitPrice",
    enabled: !isAncillary,
  });

  const totalUnitPrice = calculateTotalTsuboUnitPrice(
    Number(form.watch("rentUnitPrice")) || undefined,
    Number(form.watch("maintenanceFeeUnitPrice")) || undefined,
  );
  useEffect(() => {
    form.setValue(
      "totalRentUnitPrice",
      totalUnitPrice === undefined ? "" : String(totalUnitPrice),
      { shouldDirty: false, shouldValidate: false },
    );
  }, [form, totalUnitPrice]);
  const pricingType = form.watch("pricingType");
  const ancillaryUnitPrice = form.watch("unitPrice");
  const ancillaryQuantity = form.watch("quantity");
  useEffect(() => {
    if (!isAncillary || pricingType !== "unit_quantity") return;
    const monthlyAmount = calculateContractMonthlyAmount(
      ancillaryUnitPrice === "" ? undefined : Number(ancillaryUnitPrice),
      ancillaryQuantity === "" ? undefined : Number(ancillaryQuantity),
    );
    form.setValue(
      "monthlyRent",
      monthlyAmount === undefined ? "" : String(monthlyAmount),
      {
        shouldDirty:
          form.getFieldState("unitPrice").isDirty ||
          form.getFieldState("quantity").isDirty,
        shouldValidate: true,
      },
    );
  }, [ancillaryQuantity, ancillaryUnitPrice, form, isAncillary, pricingType]);

  const handleSubmit = form.handleSubmit(async (data) => {
    if (onSubmit) {
      setSubmitting(true);
      try {
        const r = await onSubmit({ ...data, contractorType, otherFees } as any);
        // "deferred" のときは確認モーダル待ち。このモーダルは閉じずに残す。
        if (r !== "deferred") handleClose();
      } catch {
        // エラー時はモーダルを閉じない
      } finally {
        setSubmitting(false);
      }
    }
  });

  // ── 変更検知 ──
  // フォームの全フィールドを subscribe して、initialData と差分があるかチェック
  const watchedValues = form.watch();

  const stringEq = (a: unknown, b: unknown) =>
    String(a ?? "") === String(b ?? "");

  const formChanged = (() => {
    if (!initialData) return false;
    // 契約者タイプは独立 state
    if (
      contractorType !== (initialData.contractorType || initialContractorType)
    ) {
      return true;
    }
    // 個人 / 法人で確認するキー
    const keys: Array<keyof EditContractFormData> =
      contractorType === "individual"
        ? [
            "tenantLastName",
            "tenantFirstName",
            "tenantPhone",
            "tenantEmail",
            "startDate",
            "endDate",
            "contract_form",
            "rentStartDate",
            "cancellationDate",
            "monthlyRent",
            "rentUnitPrice",
            "maintenanceFee",
            "maintenanceFeeUnitPrice",
            "deposit",
            "depositAfter",
            "keyMoney",
            "note",
          ]
        : [
            "companyName",
            "representativeLastName",
            "representativeFirstName",
            "companyPhone",
            "companyEmail",
            "startDate",
            "endDate",
            "contract_form",
            "rentStartDate",
            "cancellationDate",
            "monthlyRent",
            "rentUnitPrice",
            "maintenanceFee",
            "maintenanceFeeUnitPrice",
            "deposit",
            "depositAfter",
            "keyMoney",
            "note",
          ];
    for (const key of keys) {
      if (!stringEq(watchedValues[key], initialData[key])) {
        return true;
      }
    }
    return false;
  })();

  const otherFeesChanged = (() => {
    const initial = ((initialData as any)?.otherFees || []) as Array<{
      label: string;
      amount: string;
    }>;
    if (otherFees.length !== initial.length) return true;
    for (let i = 0; i < otherFees.length; i++) {
      if (
        !stringEq(otherFees[i]?.label, initial[i]?.label) ||
        !stringEq(otherFees[i]?.amount, initial[i]?.amount)
      ) {
        return true;
      }
    }
    return false;
  })();

  const canSubmit = formChanged || otherFeesChanged;

  return (
    <CustomModal
      openModal={isOpen}
      handleCloseModal={handleClose}
      title="契約更新"
      modalType="create"
      addTitleBorder
      confirmOnDirtyClose={form.formState.isDirty}
    >
      <Box
        component="form"
        // ポータル描画でも React 合成イベントは React ツリーを伝播するため、
        // submit が外側の <form> (PropertyOverview) に届いて別トーストが
        // 二重発火する。モーダル境界で伝播を止める。
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(e);
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          maxHeight: "80vh",
          position: "relative",
          // CustomModal の .modalBoxWrapper が padding:16px を持つので、
          // このモーダルでは pb のみ打ち消す (= 下余白 0)。
          mb: "-16px",
        }}
      >
        {submitting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              borderRadius: "8px",
            }}
          >
            <CustomLoading loading={true} fullScreen={false} size="medium" />
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: { xs: "12px 12px 0 12px", sm: "12px 12px 0 12px" },
          }}
        >
          <Stack spacing={0}>
            {/* 契約者種別（固定表示） */}
            <Box sx={{ mb: 0 }}>
              <Typography fontSize={12} fontWeight={700} mb={0.5}>
                種別
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  p: 0.5,
                  mb: 1.5,
                  borderRadius: 3,
                  bgcolor: "#F1F3F5",
                  width: "auto",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 2.5,
                    top: 4,
                    bottom: 4,
                    width: "calc((100% - 16px) / 2)",
                    transform:
                      contractorType === "individual"
                        ? "translateX(0)"
                        : "translateX(calc(100% + 8px))",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0px 1px 2px rgba(16,24,40,0.08)",
                    border: "1px solid rgba(0,0,0,0.1)",
                    pointerEvents: "none",
                  }}
                />
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 4.5,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: "not-allowed",
                    bgcolor: "transparent",
                    color: contractorType === "individual" ? "#111" : "#6B7280",
                    border: "1px solid transparent",
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    個人
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 4.5,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: "not-allowed",
                    bgcolor: "transparent",
                    color: contractorType === "corporate" ? "#111" : "#6B7280",
                    border: "1px solid transparent",
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    法人
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* 個人の場合 */}
            {contractorType === "individual" && (
              <>
                <CustomTwoColInputGroup
                  label="氏名"
                  firstName="tenantLastName"
                  lastName="tenantFirstName"
                  placeholderOne="氏"
                  placeholderTwo="名"
                  register={form.register as any}
                  errors={form.formState.errors as any}
                  isRequired={false}
                  marginBottom="0px"
                  inputWidth="100%"
                  inputWidthSp={"100%"}
                />
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="電話番号"
                    name="tenantPhone"
                    type="tel"
                    placeholder="例: 09012345678"
                    register={form.register}
                    errors={form.formState.errors}
                    isRequired={false}
                    inputWidth="100%"
                    inputMode="tel"
                  />
                </Box>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="メールアドレス"
                    name="tenantEmail"
                    type="email"
                    placeholder="例: yamada@example.com"
                    register={form.register}
                    errors={form.formState.errors}
                    isRequired={false}
                    inputWidth="100%"
                  />
                </Box>
              </>
            )}

            {/* 法人の場合 */}
            {contractorType === "corporate" && (
              <>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="法人名"
                    name="companyName"
                    placeholder="例: 株式会社○○"
                    register={form.register}
                    errors={form.formState.errors}
                    isRequired={false}
                    inputWidth="100%"
                  />
                </Box>
                <CustomTwoColInputGroup
                  label="代表者氏名"
                  firstName="representativeLastName"
                  lastName="representativeFirstName"
                  placeholderOne="氏"
                  placeholderTwo="名"
                  register={form.register as any}
                  errors={form.formState.errors as any}
                  isRequired={false}
                  marginBottom="0px"
                  inputWidth="100%"
                  inputWidthSp={"100%"}
                />
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="電話番号"
                    name="companyPhone"
                    type="tel"
                    placeholder="例: 0312345678"
                    register={form.register}
                    errors={form.formState.errors}
                    isRequired={false}
                    inputWidth="100%"
                    inputMode="tel"
                  />
                </Box>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="メールアドレス"
                    name="companyEmail"
                    type="email"
                    placeholder="例: info@company.com"
                    register={form.register}
                    errors={form.formState.errors}
                    isRequired={false}
                    inputWidth="100%"
                  />
                </Box>
              </>
            )}

            <Box sx={{ mb: 0 }}>
              <CustomCalendarInput<EditContractFormData>
                name="startDate"
                control={form.control as any}
                label="契約開始日"
                isRequired={false}
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomCalendarInput<EditContractFormData>
                name="endDate"
                control={form.control as any}
                label="契約終了日"
                isRequired={false}
                rules={{
                  validate: (value) => {
                    const startRaw = form.getValues("startDate") as string;
                    if (startRaw && value) {
                      const start = new Date(startRaw);
                      const end = new Date(value as string);
                      if (end <= start) {
                        return "契約終了日は契約開始日より後の日付である必要があります";
                      }
                    }
                    return true;
                  },
                }}
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomSingleSelect
                name="contract_form"
                control={form.control as any}
                label="契約形態"
                options={[
                  { value: "普通借", label: "普通借" },
                  { value: "定期借", label: "定期借" },
                  { value: "その他", label: "その他" },
                ]}
                isRequired={false}
                inputWidth="100%"
                inputWidthSp="100%"
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomCalendarInput<EditContractFormData>
                name="rentStartDate"
                control={form.control as any}
                label="賃料起算日"
                isRequired={false}
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomCalendarInput<EditContractFormData>
                name="cancellationDate"
                control={form.control as any}
                label="解約予定日"
                isRequired={false}
              />
            </Box>
            {isAncillary && (
              <>
                <CustomSingleSelect
                  name="pricingType"
                  control={form.control}
                  label="料金体系"
                  options={[
                    { value: "fixed", label: "固定月額" },
                    { value: "unit_quantity", label: "単価 × 数量" },
                  ]}
                  inputWidth="100%"
                />
                {pricingType === "unit_quantity" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 2,
                    }}
                  >
                    <CustomTextInput
                      label="単価（税別）"
                      name="unitPrice"
                      type="number"
                      showYen
                      register={form.register}
                      errors={form.formState.errors}
                      watch={form.watch}
                      setValue={form.setValue}
                      inputWidth="100%"
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) 120px",
                        gap: 1,
                      }}
                    >
                      <CustomTextInput
                        label="数量"
                        name="quantity"
                        type="number"
                        register={form.register}
                        errors={form.formState.errors}
                        inputWidth="100%"
                      />
                      <CustomSingleSelect
                        name="quantityUnit"
                        control={form.control}
                        label="単位"
                        options={[
                          { value: "台", label: "台" },
                          { value: "ヶ所", label: "ヶ所" },
                          { value: "基", label: "基" },
                          { value: "一式", label: "一式" },
                        ]}
                        inputWidth="100%"
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: isAncillary ? "1fr" : "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <CustomTextInput
                  label="月額賃料（税別）"
                  name="monthlyRent"
                  type="number"
                  showYen
                  watch={form.watch}
                  setValue={form.setValue}
                  placeholder="例: 150000"
                  register={form.register}
                  errors={form.formState.errors}
                  isRequired={false}
                  inputWidth="100%"
                  readOnly={isAncillary && pricingType === "unit_quantity"}
                />
              </Box>
              {!isAncillary && (
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="賃料坪単価"
                    name="rentUnitPrice"
                    type="number"
                    showYen
                    endAdornmentText="円/坪"
                    watch={form.watch}
                    setValue={form.setValue}
                    register={form.register}
                    errors={form.formState.errors}
                    disabled={!areaTsubo || areaTsubo <= 0}
                    inputWidth="100%"
                  />
                </Box>
              )}
            </Box>
            {!isAncillary && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <CustomTextInput
                      label="共益費"
                      name="maintenanceFee"
                      type="number"
                      showYen
                      watch={form.watch}
                      setValue={form.setValue}
                      placeholder="例: 10000"
                      register={form.register}
                      errors={form.formState.errors}
                      isRequired={false}
                      inputWidth="100%"
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <CustomTextInput
                      label="共益費坪単価"
                      name="maintenanceFeeUnitPrice"
                      type="number"
                      showYen
                      endAdornmentText="円/坪"
                      watch={form.watch}
                      setValue={form.setValue}
                      register={form.register}
                      errors={form.formState.errors}
                      disabled={!areaTsubo || areaTsubo <= 0}
                      inputWidth="100%"
                    />
                  </Box>
                </Box>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="賃料合計坪単価"
                    name="totalRentUnitPrice"
                    type="number"
                    showYen
                    endAdornmentText="円/坪"
                    watch={form.watch}
                    setValue={form.setValue}
                    register={form.register}
                    errors={form.formState.errors}
                    inputWidth="100%"
                    readOnly
                  />
                </Box>
              </>
            )}
            <Box sx={{ mb: 0 }}>
              <CustomTextInput
                label="敷金"
                name="deposit"
                type="number"
                showYen
                watch={form.watch}
                setValue={form.setValue}
                placeholder="例: 300000"
                register={form.register}
                errors={form.formState.errors}
                isRequired={false}
                inputWidth="100%"
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomTextInput
                label="償却後敷金"
                name="depositAfter"
                type="number"
                showYen
                watch={form.watch}
                setValue={form.setValue}
                placeholder="例: 100000"
                register={form.register}
                errors={form.formState.errors}
                isRequired={false}
                inputWidth="100%"
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomTextInput
                label="礼金"
                name="keyMoney"
                type="number"
                showYen
                watch={form.watch}
                setValue={form.setValue}
                placeholder="例: 150000"
                register={form.register}
                errors={form.formState.errors}
                isRequired={false}
                inputWidth="100%"
              />
            </Box>
            {/* その他料金 */}
            <OtherFeesInput
              fees={otherFees}
              onChange={setOtherFees}
              inputWidth="100%"
            />

            <Box sx={{ mt: 2 }}>
              <CustomTextInput
                label="備考"
                name="note"
                placeholder="契約に関する特記事項があれば記入してください"
                register={form.register}
                errors={form.formState.errors}
                multiline={true}
                rows={3}
                inputWidth="100%"
                labelSx={{ paddingTop: "0px" }}
              />
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            py: "8px",
            borderTop: "1px solid #e5e7eb",
            bgcolor: "white",
          }}
        >
          <Stack direction="row" spacing={1} justifyContent="center">
            <Can action="contract:edit">
              <CustomButton
                label="更新"
                buttonCategory="submit"
                type="submit"
                disabled={submitting || !canSubmit}
              />
            </Can>
          </Stack>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default EditContractModal;
